import React, { FunctionComponent, useState } from "react";
import { Box, Card, DialogContent, SelectChangeEvent, Toolbar, useTheme } from "@mui/material";
import styles from "./styles.module.scss";
import { strings } from "../../localization/LocalizedStrings";
import { RightDetailListItem } from "../RightDetailListItem";
import { AlertDialog } from "../AlertDialog";
import { ImageCache } from "../../models/ImageCache";
import { LanguageManager } from "../../models/LanguageManager";
import { ThemeManager, ThemeMode } from "../../models/ThemeManager";
import { SelectListItem } from "../SelectListItem";
import { AppCredits } from "../AppCredits";
import { ToolbarButton } from "../toolbar/ToolbarButton";
import { RoutesBuilder } from "../../models/RoutesBuilder";
import { DiscreetLink } from "../DiscreetLink";
import { useSessionManager, useUser } from "../../hooks/session";
import { useDeleteUser } from "../../hooks/users/useDeleteUser";
import { ModifyGenericAttributeDialog } from "../attributes/ModifyGenericAttributeDialog";
import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { StatusCodes } from "http-status-codes";
import { LoginAgainDialog } from "../LoginAgainDialog";
import { useNavigate } from "react-router";
import { DataStorage } from "../../models/DataStorage";
import { PiperDialog } from "../PiperDialog";
import { IconType } from "../../icons";
import { PiperDivider } from "../PiperDivider";

export const PreferencesDialog: FunctionComponent<IPreferencesDialogProps> = ({
  isOpen,
  setIsOpen,
  canRemoveAccount
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { closeSession } = useSessionManager();
  const [currentUser] = useUser.useState();
  const { deleteUser } = useDeleteUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isCacheAlertOpen, setIsCacheAlertOpen] = useState(false);
  const [isCacheLoading, setIsCacheLoading] = useState(false);
  const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] = useState(false);
  const [isSignInAgainDialogOpen, setIsSignInAgainDialogOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const handleClose = () => {
    if (isLoading) return;
    setIsOpen(false);
  };
  const handleThemeChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as ThemeMode;
    ThemeManager.setTheme(newValue);
  };
  const handleLanguageChange = (event: SelectChangeEvent) => {
    const isEnglish = event.target.value;
    if (isEnglish !== null) {
      LanguageManager.setLanguage(isEnglish ? "en-US" : "es-ES");
      window.location.reload();
    }
  };
  const deleteAccount = async () => {
    setIsLoading(true);
    const { status, body } = await deleteUser();
    if (StatusCodesHelper.isSuccessful(status)) {
      closeSession();
      navigate(RoutesBuilder.signIn());
    } else if (status == StatusCodes.FORBIDDEN) {
      setIsSignInAgainDialogOpen(true);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <PiperDialog fullWidth open={isOpen} onClose={handleClose}>
      <Toolbar>
        <h3>{strings.settings_preferences_title}</h3>
        <Box flexGrow={1} />
        <ToolbarButton
          loading={isLoading}
          onClick={handleClose}
          tooltip={strings.close}
          iconType={IconType.Close}
        />
      </Toolbar>
      <DialogContent style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
        <Card className={styles.section}>
          <SelectListItem
            key={"appearance"}
            iconType={IconType.Routine}
            title={strings.theme_option}
            labels={ThemeManager.listOfThemeNames()}
            values={ThemeManager.listOfThemeModes()}
            value={ThemeManager.currentThemeMode()}
            handleChange={handleThemeChange}
            disabled={isLoading}
          />
          <PiperDivider />
          <SelectListItem
            key={"language"}
            iconType={IconType.Translate}
            title={strings.language_title}
            subtitle={strings.language_global_title}
            labels={["English", "Español"]}
            values={[true, false]}
            value={strings.locale == "en-US"}
            handleChange={handleLanguageChange}
            disabled={isLoading}
          />
        </Card>
        {currentUser && canRemoveAccount && (
          <Card className={styles.section}>
            <RightDetailListItem
              key={"remove_account"}
              iconType={IconType.AccountCircleOff}
              title={strings.remove_account_title}
              titleColor={theme.palette.colors?.remove}
              onClick={() => setIsDeleteAccountDialogOpen(true)}
              disabled={isLoading}
            />
          </Card>
        )}
        <Card className={styles.section}>
          <a href={`mailto: ${strings.support_mail}`} style={{ textDecoration: "none" }}>
            <RightDetailListItem
              key={"mail_support"}
              iconType={IconType.Mail}
              title={strings.contact_support_title}
              leftSubtitle={strings.support_mail}
              disabled={isLoading}
            />
          </a>
        </Card>
        <Card className={styles.section}>
          <RightDetailListItem
            key={"clear_cache"}
            iconType={IconType.Memory}
            title={strings.clear_cache_title}
            detail={""}
            isLoading={isCacheLoading}
            onClick={() => {
              setIsCacheAlertOpen(true);
            }}
            disabled={isLoading}
          />
          <PiperDivider />
          <DiscreetLink
            to={`${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.about()}`}
            target={"_blank"}
          >
            <RightDetailListItem
              key={"about"}
              iconType={IconType.Info}
              title={strings.about_title}
              disabled={isLoading}
            />
          </DiscreetLink>
          <PiperDivider />
          <RightDetailListItem
            key={"app_version"}
            iconType={IconType.LaptopMac}
            title={strings.app_version_title}
            detail={process.env.VERSION}
            disabled={isLoading}
          />
        </Card>
        <AppCredits className={styles.credits} />
      </DialogContent>
      <AlertDialog
        message={strings.settings_cache_confirmation}
        open={isCacheAlertOpen}
        setOpen={setIsCacheAlertOpen}
        isConfirm
        onConfirm={async () => {
          setIsCacheLoading(true);
          await ImageCache.shared.clear();
          DataStorage.reset();
          setIsCacheLoading(false);
        }}
      />
      <ModifyGenericAttributeDialog
        title={strings.remove_account_title}
        description={strings.remove_account_confirmation_message}
        placeholder={strings.remove_account_title}
        missingText={strings.field_missing_short}
        canClear={false}
        open={isDeleteAccountDialogOpen}
        onClose={() => setIsDeleteAccountDialogOpen(false)}
        isNumber={false}
        onChange={deleteAccount}
        matchText={strings.remove_account_title}
      />
      <LoginAgainDialog
        isOpen={isSignInAgainDialogOpen}
        setIsOpen={setIsSignInAgainDialogOpen}
        loginCallback={async () => {
          setIsSignInAgainDialogOpen(false);
          await deleteAccount();
        }}
      />
      <AlertDialog
        title={strings.app_name}
        message={alertMessage}
        open={isAlertOpen}
        setOpen={setIsAlertOpen}
      />
    </PiperDialog>
  );
};

export interface IPreferencesDialogProps {
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  canRemoveAccount: boolean;
}
